/**
 * Meta-Pixel Helper. Pixel-Init liegt in `index.html` (ID 829299860547224).
 * Diese Datei kapselt das Feuern von Standard-Events inkl. Dedupe und
 * Dev-Logging, damit wir den Funnel im Browser vor dem ersten Werbe-Euro
 * verifizieren können.
 */

import { inUnsererApp } from "@/lib/app/umgebung";
import { getAttribution } from "@/lib/marketing/attribution";
import { safeSessionGetItem, safeSessionSetItem } from "@/lib/safeStorage";

type Fbq = ((...args: unknown[]) => void) & { loaded?: boolean };

function getFbq(): Fbq | null {
  if (typeof window === "undefined") return null;
  const fbq = (window as unknown as { fbq?: Fbq }).fbq;
  return typeof fbq === "function" ? fbq : null;
}

function isDev(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

function devLog(eventName: string, payload: Record<string, unknown>): void {
  if (!isDev()) return;
  const utm_campaign =
    (payload.utm_campaign as string | undefined) ??
    getAttribution()?.utm_campaign ??
    null;
  // eslint-disable-next-line no-console
  console.log("[Meta Pixel]", eventName, { utm_campaign, ...payload });
}

export function trackViewContent(payload: Record<string, unknown> = {}): void {
  const fbq = getFbq();
  const merged = { ...payload };
  devLog("ViewContent", merged);
  fbq?.("track", "ViewContent", merged);
}

export function trackLead(payload: Record<string, unknown> = {}): void {
  const fbq = getFbq();
  const attr = getAttribution();
  const merged = {
    utm_campaign: attr?.utm_campaign ?? undefined,
    utm_source: attr?.utm_source ?? undefined,
    ...payload,
  };
  devLog("Lead", merged);
  fbq?.("track", "Lead", merged);
}

const COMPLETE_REGISTRATION_KEY = "mk_pixel_complete_registration_fired";

/**
 * Feuert `CompleteRegistration` genau einmal pro Browser-Session.
 * Wird im Direct-Session-Pfad von `Registrieren.tsx` nach erfolgreichem
 * Account-Setup aufgerufen.
 */
export function trackCompleteRegistration(
  payload: Record<string, unknown> = {},
): void {
  const alreadyFired = safeSessionGetItem(COMPLETE_REGISTRATION_KEY) === "1";
  if (alreadyFired) {
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.log("[Meta Pixel] CompleteRegistration übersprungen (bereits gefeuert)");
    }
    return;
  }

  const fbq = getFbq();
  const attr = getAttribution();
  const merged = {
    content_name: "Free Registrierung",
    content_category: attr?.utm_campaign ?? "organic",
    utm_campaign: attr?.utm_campaign ?? undefined,
    utm_source: attr?.utm_source ?? undefined,
    ...payload,
  };

  devLog("CompleteRegistration", merged);
  fbq?.("track", "CompleteRegistration", merged);
  safeSessionSetItem(COMPLETE_REGISTRATION_KEY, "1");
}

/**
 * Kostenlos starten (KS1, docs/strategie/kostenlos-starten.md Abschnitt 3): ein Kauf als Meta-Ereignis mit Betrag.
 * `Subscribe` für Mitgliedschaft (auch der Beginn der Testphase) und Kompakt, `Purchase` für Taler-Paket und
 * Ausbildung. Nie in der App (`inUnsererApp()`, Apple 3.1.3). Mit `ref` (Abo, Rechnung, Paket) höchstens einmal je
 * Sitzung, die `eventID` lässt später ein Ereignis vom Server zusammenführen. Nicht verwechseln mit dem
 * `Subscribe` ohne Betrag der Newsletter-Seiten.
 */
export type KaufArt = "mitgliedschaft" | "kompakt" | "taler_paket" | "ausbildung";

export const KAUF_EREIGNIS: Record<KaufArt, "Subscribe" | "Purchase"> = {
  mitgliedschaft: "Subscribe",
  kompakt: "Subscribe",
  taler_paket: "Purchase",
  ausbildung: "Purchase",
};

export function trackKauf(art: KaufArt, betragEuro: number, angaben: Record<string, unknown> = {}, ref?: string): void {
  if (inUnsererApp()) return;
  if (!Number.isFinite(betragEuro) || betragEuro < 0) return;
  const schluessel = ref ? `mk_pixel_kauf:${art}:${ref}` : null;
  if (schluessel && safeSessionGetItem(schluessel) === "1") return;
  const attr = getAttribution();
  const name = KAUF_EREIGNIS[art];
  const merged = {
    value: Math.round(betragEuro * 100) / 100,
    currency: "EUR",
    content_category: art,
    utm_campaign: attr?.utm_campaign ?? undefined,
    utm_source: attr?.utm_source ?? undefined,
    ...angaben,
  };
  devLog(name, merged);
  const fbq = getFbq();
  if (ref) fbq?.("track", name, merged, { eventID: `${art}:${ref}` });
  else fbq?.("track", name, merged);
  if (schluessel) safeSessionSetItem(schluessel, "1");
}
